// Persistent per-part progress in localStorage so Reading home can show
// "done" state + scores + mistakes counts across page reloads.
//
// Schema rewritten 2026-05-06 to match the new 5-part FET reading layout
// (Signs / Gap Fill / Text Matching / People ↔ Place / Long Reading).

const STORAGE_KEY = 'fet_reading_progress';

export function getPartProgress() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function setPartProgress(partNumber, result) {
  if (!partNumber) return;
  try {
    const all = getPartProgress();
    all[String(partNumber)] = {
      score: Number(result?.score ?? 0),
      max: Number(result?.max ?? 0),
      ts: Date.now(),
      mistakes: Array.isArray(result?.mistakes) ? result.mistakes : [],
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {
    // ignore quota / privacy-mode errors
  }
}

export function clearPartProgress() {
  try { window.localStorage.removeItem(STORAGE_KEY); } catch {}
}

export function findNextPart(parts, currentPartNumber) {
  if (!Array.isArray(parts) || !parts.length) return null;
  const sorted = parts
    .filter((p) => p && p.has_content && Number.isFinite(p.part_number))
    .sort((a, b) => a.part_number - b.part_number);
  const next = sorted.find((p) => p.part_number > currentPartNumber);
  return next || null;
}

// Score a single reading part client-side from its content + the user's
// answer map. Mirrors the new backend's scoring logic so we don't depend on
// the backend running the post-migration code: while the legacy backend is
// live it returns 0 for everything (looks at old field names), and even the
// correct count for Part 1 reads `sign.answer` instead of `sign.correct`.
// We trust the client here so practice + exam mode show real scores today.
const norm = (v) => String(v ?? '').trim().toLowerCase();

export function scoreReadingPart(partNumber, content, answers) {
  const c = content || {};
  const a = answers || {};
  let score = 0;
  let max = 0;
  switch (Number(partNumber)) {
    case 1: {
      const signs = Array.isArray(c.signs) ? c.signs : [];
      signs.forEach((s, i) => {
        max += 1;
        const expected = norm(s?.correct ?? s?.answer);
        if (expected && norm(a[`sign_${i}`]) === expected) score += 1;
      });
      return { score, max };
    }
    case 2: {
      const gaps = Array.isArray(c.gaps) ? c.gaps : [];
      gaps.forEach((g, i) => {
        max += 1;
        const expected = norm(g?.correct);
        if (expected && norm(a[`gap_${i}`]) === expected) score += 1;
      });
      return { score, max };
    }
    case 3: {
      const qs = Array.isArray(c.questions) ? c.questions : [];
      qs.forEach((q, i) => {
        max += 1;
        const expected = norm(q?.correct);
        if (expected && norm(a[`q_${i}`]) === expected) score += 1;
      });
      return { score, max };
    }
    case 4: {
      const items = Array.isArray(c.items) ? c.items : [];
      items.forEach((it, i) => {
        max += 1;
        const expected = norm(it?.correct);
        if (expected && norm(a[`q_${i}`]) === expected) score += 1;
      });
      return { score, max };
    }
    case 5: {
      const qs = Array.isArray(c.questions) ? c.questions : [];
      qs.forEach((q, i) => {
        max += 1;
        const expected = norm(q?.correct);
        if (expected && norm(a[`q_${i}`]) === expected) score += 1;
      });
      return { score, max };
    }
    default:
      return { score: 0, max: 0 };
  }
}

// Build a normalized mistake entry for a given part's content + the user's
// answer map. Each Part screen calls this when finishing so Reading home can
// surface "Mistakes" without re-deriving from raw answers.
//
//   mistakes[i] = {
//     qIdx          — index inside that part
//     prompt        — short label / question text (best available)
//     yourAnswer    — what the user picked / typed (already humanised)
//     correctAnswer — the right answer (humanised)
//     why_en        — admin-authored explanation (English), if present
//     why_ar        — admin-authored explanation (Arabic), if present
//   }
export function computeMistakes(partNumber, content, answers) {
  const c = content || {};
  const a = answers || {};
  const out = [];

  switch (Number(partNumber)) {
    case 1: {
      // Signs & Notices — sign_{i} → letter
      const signs = c.signs || [];
      signs.forEach((s, i) => {
        const picked = a[`sign_${i}`];
        const correct = String(s.correct || s.answer || '').toUpperCase();
        if (!correct || picked === correct) return;
        const opts = s.options || [];
        const lookup = (letter) => {
          const m = opts.find((o) => String(o.letter || '').toUpperCase() === letter);
          return m?.text || '';
        };
        out.push({
          qIdx: i,
          prompt: s.question || s.body || `Sign ${i + 1}`,
          yourAnswer: picked ? `${picked}. ${lookup(picked)}` : 'Not answered',
          correctAnswer: `${correct}. ${lookup(correct)}`,
          why_en: s.why_en || null,
          why_ar: s.why_ar || null,
        });
      });
      return out;
    }
    case 2: {
      // Gap Fill — gap_{i} → option string
      const gaps = c.gaps || [];
      gaps.forEach((g, i) => {
        const picked = a[`gap_${i}`];
        const correct = String(g.correct || '');
        if (!correct || (picked && picked.toLowerCase() === correct.toLowerCase())) return;
        out.push({
          qIdx: i,
          prompt: `Gap ${g.n || i + 1}`,
          yourAnswer: picked || 'Not answered',
          correctAnswer: correct,
          why_en: g.why_en || null,
          why_ar: g.why_ar || null,
        });
      });
      return out;
    }
    case 3: {
      // Text Matching — q_{i} → person id
      const people = c.people || [];
      const questions = c.questions || [];
      const nameOf = (id) => (people.find((p) => p.id === id) || {}).name || id || '';
      questions.forEach((q, i) => {
        const picked = a[`q_${i}`];
        const correct = String(q.correct || '').toLowerCase();
        if (!correct || (picked && picked.toLowerCase() === correct)) return;
        out.push({
          qIdx: i,
          prompt: q.text || `Question ${i + 1}`,
          yourAnswer: picked ? nameOf(picked) : 'Not answered',
          correctAnswer: nameOf(correct),
          why_en: q.why_en || null,
          why_ar: q.why_ar || null,
        });
      });
      return out;
    }
    case 4: {
      // People ↔ Place — q_{i} → person id (within item.people)
      const items = c.items || [];
      items.forEach((it, i) => {
        const picked = a[`q_${i}`];
        const correct = String(it.correct || '').toLowerCase();
        if (!correct || (picked && picked.toLowerCase() === correct)) return;
        const people = Array.isArray(it.people) ? it.people : [];
        const nameOf = (id) => (people.find((p) => p.id === id) || {}).name || id || '';
        out.push({
          qIdx: i,
          prompt: it.place?.name || `Place ${i + 1}`,
          yourAnswer: picked ? nameOf(picked) : 'Not answered',
          correctAnswer: nameOf(correct),
          why_en: it.why_en || null,
          why_ar: it.why_ar || null,
        });
      });
      return out;
    }
    case 5: {
      // Long Reading — q_{i} → letter
      const questions = c.questions || [];
      questions.forEach((q, i) => {
        const picked = a[`q_${i}`];
        const correct = String(q.correct || '').toUpperCase();
        if (!correct || picked === correct) return;
        const opts = q.options || [];
        const lookup = (letter) => {
          const m = opts.find((o) => String(o.letter || '').toUpperCase() === letter);
          return m?.text || '';
        };
        out.push({
          qIdx: i,
          prompt: q.text || `Question ${i + 1}`,
          yourAnswer: picked ? `${picked}. ${lookup(picked)}` : 'Not answered',
          correctAnswer: `${correct}. ${lookup(correct)}`,
          why_en: q.why_en || null,
          why_ar: q.why_ar || null,
        });
      });
      return out;
    }
    default:
      return [];
  }
}
